import type { ReactNode } from "react";
import { Flex, theme } from "antd";
import styled from "styled-components";
import { useTranslate } from "@refinedev/core";
import { TrashIcon } from "@phosphor-icons/react";
import { Button, Card, Pill, Popconfirm } from "@libertify/ui";

export type EditableItemCardProps = {
  /** The numbered chip, e.g. "POINT 1" / "CARD 1". */
  label: ReactNode;
  /** Label of the remove action. */
  removeLabel: string;
  /** Question the confirmation asks, e.g. "Remove this card?". */
  removeConfirmLabel: string;
  /**
   * Ask before removing. Cards autosave, so anything the item already holds
   * would be gone for good — a blank, just-added row goes without asking.
   */
  confirmRemove: boolean;
  onRemove: () => void;
  /** The item's editable fields. */
  children: ReactNode;
};

/**
 * One removable, numbered item of an edit card's list (a key point, a quick
 * recall card): the chip, a Remove action, and whatever fields the caller
 * renders below. Holds the shared look only — the fields, and how they are
 * bound, stay with each card.
 */
export const EditableItemCard = ({
  label,
  removeLabel,
  removeConfirmLabel,
  confirmRemove,
  onRemove,
  children,
}: EditableItemCardProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const removeButton = (
    <Button
      variant="tertiary"
      size="s"
      leftIcon={<TrashIcon size={14} />}
      onClick={confirmRemove ? undefined : onRemove}
    >
      {removeLabel}
    </Button>
  );

  return (
    <Card
      padding="medium"
      bordered
      shadow="none"
      style={{ background: token.colorFillQuaternary }}
    >
      <Flex vertical gap={12}>
        <Flex align="center" justify="space-between" gap={8}>
          <ItemNumber>{label}</ItemNumber>
          {confirmRemove ? (
            <Popconfirm
              title={removeConfirmLabel}
              onConfirm={onRemove}
              confirmText={t("buttons.delete")}
              cancelText={t("buttons.cancel")}
              danger
            >
              {removeButton}
            </Popconfirm>
          ) : (
            removeButton
          )}
        </Flex>
        {children}
      </Flex>
    </Card>
  );
};

export default EditableItemCard;

// ─── Styled components ───

const ItemNumber = styled(Pill)`
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;
